// What? A React component for managing document types in the admin interface.
// Why? To provide administrators with a central location to view, create, edit, publish, and delete document types.
// How? The component's logic flow:
// 1. Initialize: Fetch all document types for the workspace
// 2. Display Management: Show document types in responsive table (desktop) or cards (mobile)
// 3. Document Type Actions: Handle edit, publish, and delete operations
// 4. Modal Handling: Manage confirmation modals for publish, delete, and edit actions
// 5. Version Control: Handle creating new versions for published document types
// 6. Error Handling: Display operation errors and provide retry mechanisms

import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Table,
  Box,
  Chip,
  CircularProgress,
  Typography,
  Stack,
  Card,
  CardContent,
  Alert,
  IconButton,
  Snackbar,
} from "@mui/joy";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import Tooltip from "@mui/joy/Tooltip";

import Page from "../../../../components/Pages/Page";
import PageHeader from "../../../../components/PageHeader";
import Loading from "../../../../components/Loading";
import PageError from "../../../../components/PageError";
import ConfirmationModal from "../../../../components/Modals/ConfirmationModal";
import {
  useTutorialTarget,
  useTutorialHint,
} from "../../../../components/Tutorial";
import {
  useFetchDocumentTypes,
  useDeleteDocumentType,
  usePublishDocumentType,
  useCreateNewDocumentTypeVersion,
} from "../../hooks/useDocumentTypes";
import { DocumentTypeResponse } from "../../types/documentTypes";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";
import CopyDocumentTypeDialog from "../../components/CopyDocumentTypeDialog";
import { useDocumentTypePortability } from "../../hooks/useDocumentTypePortability";

const DocumentTypesOverview: React.FC = () => {
  const { workspace, isAdmin } = useWorkspaceContext();
  const workspace_id = workspace?.id || "";
  const { documentTypes, loading, error, refresh } =
    useFetchDocumentTypes(workspace_id);

  const addDocTypeRef = useTutorialTarget("meridian-add-doc-type");
  useTutorialHint(
    {
      id: "meridian-first-doc-type",
      targetId: "meridian-add-doc-type",
      title: "Create your first Document Type",
      body: "Document types define the structure and inputs for AI-generated documents. Create one here to get started.",
      placement: "bottom",
      scope: "workspace",
    },
    !loading && !error && (!documentTypes || documentTypes.length === 0),
  );
  const importHintRef = useTutorialTarget("meridian-dt-import-button");
  useTutorialHint(
    {
      id: "meridian-dt-import-portability-v2",
      targetId: "meridian-dt-import-button",
      title: "New: Import a Document Type",
      body: "Import a Document Type that was saved with the Export Document Type button (found when editing a Document Type).",
      placement: "bottom",
    },
    isAdmin,
  );
  const { deleteDocumentType, loading: deleteLoading } =
    useDeleteDocumentType(workspace_id);
  const { publishDocumentType, loading: publishLoading } =
    usePublishDocumentType(workspace_id);
  const { createNewDocumentTypeVersion, loading: createVersionLoading } =
    useCreateNewDocumentTypeVersion(workspace_id);
  const { importDocumentType, isImporting } =
    useDocumentTypePortability(workspace_id);
  const importInputRef = useRef<HTMLInputElement>(null);

  // Enhanced error state management
  const [operationError, setOperationError] = useState<string | null>(null);

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    documentType?: DocumentTypeResponse;
  }>({ isOpen: false });
  const [publishModal, setPublishModal] = useState<{
    isOpen: boolean;
    documentType?: DocumentTypeResponse;
  }>({ isOpen: false });
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    documentType?: DocumentTypeResponse;
  }>({ isOpen: false });
  const [copyModal, setCopyModal] = useState<{
    isOpen: boolean;
    documentType?: DocumentTypeResponse;
  }>({ isOpen: false });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleCopyClick = (docType: DocumentTypeResponse) => {
    setCopyModal({ isOpen: true, documentType: docType });
  };

  const handleCopySuccess = (message: string) => {
    setSuccessMessage(message);
    refresh();
  };

  const handleDeleteClick = (docType: DocumentTypeResponse) => {
    setDeleteModal({ isOpen: true, documentType: docType });
  };

  const handleDelete = async () => {
    if (deleteModal.documentType) {
      try {
        await deleteDocumentType(deleteModal.documentType.DocumentTypeID);
        setDeleteModal({ isOpen: false });
        refresh();
        setOperationError(null); // Clear any previous errors
      } catch (error: any) {
        console.error("Failed to delete document type:", error);
        setOperationError(
          `Failed to delete document type: ${error.message || "Unknown error"}`,
        );
        // Don't close modal on error
      }
    }
  };

  const handlePublishClick = (docType: DocumentTypeResponse) => {
    setPublishModal({ isOpen: true, documentType: docType });
  };

  const handlePublish = async () => {
    if (publishModal.documentType) {
      try {
        await publishDocumentType(publishModal.documentType.DocumentTypeID);
        setPublishModal({ isOpen: false });
        refresh();
        setOperationError(null); // Clear any previous errors
      } catch (error: any) {
        console.error("Failed to publish document type:", error);
        setOperationError(
          `Failed to publish document type: ${error.message || "Unknown error"}`,
        );
        // Don't close modal on error
      }
    }
  };

  const handleImportClick = () => {
    setOperationError(null);
    importInputRef.current?.click();
  };

  const handleImportFile = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    try {
      const created = await importDocumentType(file);
      navigate(
        `/workspace/${encodeURIComponent(workspace_id)}/meridian/admin/document-types/${created.DocumentTypeID}`,
      );
    } catch (error: any) {
      console.error("Failed to import document type:", error);
      setOperationError(
        error.message ||
          "Something went wrong while importing this document type. Please try again.",
      );
    }
  };

  const handleEditClick = (docType: DocumentTypeResponse) => {
    const id = docType?.DocumentTypeID;
    const status = docType?.Status?.toString().trim().toLowerCase();
    if (!workspace_id || !id) {
      setOperationError("Invalid workspace or document type.");
      return;
    }

    const isPublished = status === "published";
    // Optionally: only force new version for the latest published version
    if (isPublished) {
      setEditModal({ isOpen: true, documentType: docType });
    } else {
      navigate(
        `/workspace/${encodeURIComponent(workspace_id)}/meridian/admin/document-types/${id}`,
      );
    }
  };

  const handleEditConfirm = async () => {
    if (editModal.documentType) {
      try {
        const newVersion = await createNewDocumentTypeVersion(
          editModal.documentType.DocumentTypeID,
        );
        setEditModal({ isOpen: false });
        navigate(
          `/workspace/${workspace_id}/meridian/admin/document-types/${newVersion.DocumentTypeID}`,
        );
        setOperationError(null); // Clear any previous errors
      } catch (error: any) {
        console.error("Failed to create new version:", error);
        setOperationError(
          `Failed to create new version: ${error.message || "Unknown error"}`,
        );
        // Don't close modal on error - keep it open so user can try again
      }
    }
  };

  // Helper function for status chip colors
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "published":
        return "success";
      case "draft":
        return "neutral";
      default:
        return "neutral";
    }
  };

  // Component for individual document type cards (mobile view)
  const DocumentTypeCard = ({ docType }: { docType: DocumentTypeResponse }) => (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: "sm",
          borderColor: "primary.300",
        },
      }}
    >
      <Stack spacing={2}>
        {/* Top row: Name and Status */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              level="title-sm"
              sx={{
                fontWeight: 600,
                wordBreak: "break-word",
                lineHeight: 1.3,
              }}
            >
              {docType.Name}
            </Typography>
            <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
              v{docType.Version}
            </Typography>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Chip
              size="sm"
              variant="soft"
              color={getStatusColor(docType.Status || "Draft") as any}
            >
              {docType.Status || "Draft"}
            </Chip>
          </Box>
        </Stack>

        {/* Middle row: Description and Created Date */}
        <Stack spacing={1}>
          {docType.Description && (
            <Typography
              level="body-sm"
              color="neutral"
              sx={{
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {docType.Description}
            </Typography>
          )}
          <Typography level="body-xs" color="neutral">
            <Typography
              component="span"
              level="body-xs"
              color="neutral"
              sx={{ fontWeight: 500 }}
            >
              Created:{" "}
            </Typography>
            {new Date(docType.CreatedDate).toLocaleDateString()}
          </Typography>
        </Stack>

        {/* Bottom row: Actions */}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Tooltip title="Edit">
            <IconButton
              size="sm"
              variant="plain"
              onClick={() => handleEditClick(docType)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy">
            <IconButton
              size="sm"
              variant="plain"
              onClick={() => handleCopyClick(docType)}
            >
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              docType.Status === "Published" ? "Already Published" : "Publish"
            }
          >
            <span>
              <IconButton
                size="sm"
                variant="plain"
                onClick={() => handlePublishClick(docType)}
                disabled={docType.Status === "Published" || publishLoading}
              >
                {publishLoading ? (
                  <CircularProgress size="sm" />
                ) : (
                  <PublishIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="sm"
              variant="plain"
              onClick={() => handleDeleteClick(docType)}
              disabled={deleteLoading}
            >
              <DeleteIcon sx={{ color: "#c41c1c" }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );

  if (!workspace?.id) return <PageError>Workspace not specified.</PageError>;

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">Document Types</Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <input
            ref={importInputRef}
            type="file"
            accept=".zip"
            hidden
            onChange={handleImportFile}
          />
          {isAdmin && (
            <Tooltip
              title="Upload a .zip created with the Export Document Type button"
              placement="bottom"
            >
              <span ref={importHintRef}>
                <Button
                  variant="outlined"
                  color="neutral"
                  startDecorator={<UploadIcon />}
                  loading={isImporting}
                  onClick={handleImportClick}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    justifyContent: { xs: "center", sm: "flex-start" },
                  }}
                >
                  Import Document Type
                </Button>
              </span>
            </Tooltip>
          )}
          <span ref={addDocTypeRef}>
            <Button
              variant="solid"
              color="primary"
              startDecorator={<AddIcon />}
              component={Link}
              to={`/workspace/${workspace_id}/meridian/admin/document-types/new`}
              sx={{
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              Add New Document Type
            </Button>
          </span>
        </Stack>
      </PageHeader>

      {/* Operation Error Display */}
      {operationError && (
        <Alert
          color="danger"
          variant="soft"
          sx={{ mb: 2 }}
          endDecorator={
            <IconButton
              variant="plain"
              size="sm"
              color="danger"
              onClick={() => setOperationError(null)}
            >
              <CloseIcon />
            </IconButton>
          }
        >
          {operationError}
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Loading />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography level="body-md" sx={{ mb: 2 }}>
                Error loading document types: {error}
              </Typography>
              <Button variant="outlined" onClick={refresh}>
                Retry
              </Button>
            </Box>
          ) : !documentTypes || documentTypes.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography level="body-md" sx={{ mb: 1 }}>
                No document types found
              </Typography>
              <Typography level="body-sm" color="neutral" sx={{ mb: 3 }}>
                Create your first document type to get started
              </Typography>
              <Button
                variant="solid"
                color="primary"
                startDecorator={<AddIcon />}
                component={Link}
                to={`/workspace/${workspace_id}/meridian/admin/document-types/new`}
              >
                Add New Document Type
              </Button>
            </Box>
          ) : (
            <>
              {/* Desktop Table View */}
              <Box sx={{ display: { xs: "none", lg: "block" } }}>
                <Table sx={{ "& td, & th": { verticalAlign: "top" } }}>
                  <thead>
                    <tr>
                      <th style={{ width: "25%" }}>Name</th>
                      <th>Description</th>
                      <th style={{ width: "80px" }}>Version</th>
                      <th style={{ width: "110px" }}>Status</th>
                      <th style={{ width: "120px" }}>Created</th>
                      <th style={{ width: "180px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentTypes?.map((docType) => (
                      <tr key={docType.DocumentTypeID}>
                        <td>{docType.Name}</td>
                        <td>
                          <Typography
                            level="body-sm"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "300px",
                            }}
                            title={docType.Description || ""}
                          >
                            {docType.Description || ""}
                          </Typography>
                        </td>
                        <td>v{docType.Version}</td>
                        <td>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={
                              getStatusColor(docType.Status || "Draft") as any
                            }
                          >
                            {docType.Status || "Draft"}
                          </Chip>
                        </td>
                        <td>
                          {new Date(docType.CreatedDate).toLocaleDateString()}
                        </td>
                        <td>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="sm"
                                variant="plain"
                                onClick={() => handleEditClick(docType)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy">
                              <IconButton
                                size="sm"
                                variant="plain"
                                onClick={() => handleCopyClick(docType)}
                              >
                                <ContentCopyIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                docType.Status === "Published"
                                  ? "Already Published"
                                  : "Publish"
                              }
                            >
                              <span>
                                <IconButton
                                  size="sm"
                                  variant="plain"
                                  onClick={() => handlePublishClick(docType)}
                                  disabled={
                                    docType.Status === "Published" ||
                                    publishLoading
                                  }
                                >
                                  {publishLoading ? (
                                    <CircularProgress size="sm" />
                                  ) : (
                                    <PublishIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="sm"
                                variant="plain"
                                onClick={() => handleDeleteClick(docType)}
                                disabled={deleteLoading}
                              >
                                <DeleteIcon sx={{ color: "#c41c1c" }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Box>

              {/* Mobile Card View */}
              <Box sx={{ display: { xs: "block", lg: "none" } }}>
                <Stack spacing={2}>
                  {documentTypes?.map((docType) => (
                    <DocumentTypeCard
                      key={docType.DocumentTypeID}
                      docType={docType}
                    />
                  ))}
                </Stack>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Publish Confirmation Modal */}
      <ConfirmationModal
        open={publishModal.isOpen}
        title="Publish Document Type"
        message={
          <Box>
            <Typography level="body-md" sx={{ mb: 2 }}>
              Are you sure you want to publish "
              {publishModal.documentType?.Name}"?
            </Typography>
            <Typography level="body-md" sx={{ mb: 1, fontWeight: "md" }}>
              Publishing will:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 0, "& li": { mb: 0.5 } }}>
              <li>Make this version the default version for users</li>
              <li>Lock this version from further edits</li>
              <li>Require creating a new version for any future changes</li>
            </Box>
          </Box>
        }
        actionLabel={publishLoading ? "Publishing..." : "Publish"}
        cancelLabel="Cancel"
        onAction={handlePublish}
        onClose={() => {
          setPublishModal({ isOpen: false });
          setOperationError(null);
        }}
        loading={publishLoading}
        error={publishModal.isOpen ? operationError : null}
        spinner={<CircularProgress size="sm" />}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteModal.isOpen}
        title="Delete Document Type"
        message={`Are you sure you want to delete "${deleteModal.documentType?.Name}"? This action cannot be undone.`}
        actionLabel={deleteLoading ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        onAction={handleDelete}
        onClose={() => {
          setDeleteModal({ isOpen: false });
          setOperationError(null);
        }}
        loading={deleteLoading}
        error={deleteModal.isOpen ? operationError : null}
        spinner={<CircularProgress size="sm" />}
      />

      {/* Edit Published Document Type Modal */}
      <ConfirmationModal
        open={editModal.isOpen}
        title="Create New Version"
        message={
          <Box>
            <Typography level="body-md" sx={{ mb: 2 }}>
              You are about to edit "{editModal.documentType?.Name}" which is
              currently published.
            </Typography>
            <Typography level="body-md" sx={{ mb: 1, fontWeight: "md" }}>
              To edit this document type you will:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 0, "& li": { mb: 0.5 } }}>
              <li>Create a new draft version for editing</li>
              <li>
                Keep the current published version active for users to use
              </li>
              <li>
                Be able to make and test changes in the new version before
                publishing it
              </li>
            </Box>
          </Box>
        }
        actionLabel={
          createVersionLoading ? "Creating..." : "Create New Version"
        }
        cancelLabel="Cancel"
        onAction={handleEditConfirm}
        onClose={() => {
          setEditModal({ isOpen: false });
          setOperationError(null);
        }}
        loading={createVersionLoading}
        error={editModal.isOpen ? operationError : null}
        spinner={<CircularProgress size="sm" />}
      />

      {/* Copy Document Type Dialog */}
      {copyModal.documentType && (
        <CopyDocumentTypeDialog
          open={copyModal.isOpen}
          documentTypeId={copyModal.documentType.DocumentTypeID}
          documentTypeName={copyModal.documentType.Name}
          documentTypeDescription={
            copyModal.documentType.Description || undefined
          }
          sourceWorkspaceId={workspace_id}
          sourceWorkspaceName={workspace?.name}
          onClose={() => setCopyModal({ isOpen: false })}
          onSuccess={handleCopySuccess}
        />
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        color="success"
        variant="soft"
        startDecorator={<CheckCircleIcon />}
      >
        {successMessage}
      </Snackbar>
    </Page>
  );
};

export default DocumentTypesOverview;
